import { useRef } from 'react'
import Image from 'next/legacy/image'
import { fetchBretzelRes } from '@/services/api/bretzel'
import Link from 'next/link'
import Script from 'next/script'
import useTranslation from '@/hooks/translation'

// Components
import Head from '@/components/Head'
import CenterLayout from '@/layouts/center'
import Button from '@/components/ui/button'
import Faq from '@/components/DynamicFaq'
import TrustpilotCarousel from '@/components/TrustpilotCarousel'

// Assets
import LogoANCVClassic from '@/assets/svg/ancv/ancv-classic.svg'
import HeaderANCV from '@/assets/img/ancv-classic.png'
import ANCVMobile from '@/assets/img/ancv/phone.png'
import FlyWithANCV from '@/assets/img/ancv/flywith.png'

import AircanadaLogo from '@/assets/img/ancv/aircanada.png'
import AirfranceLogo from '@/assets/img/ancv/airfrance.png'
import BritishLogo from '@/assets/img/ancv/british.png'
import EasyjetLogo from '@/assets/img/ancv/easyjet.png'
import DeltaLogo from '@/assets/img/ancv/delta.png'
import KlmLogo from '@/assets/img/ancv/klm.png'
import QatarLogo from '@/assets/img/ancv/qatar.png'
import LufthansaLogo from '@/assets/img/ancv/lufthansa.png'
import TransaviaLogo from '@/assets/img/ancv/transavia.png'
import TurkishLogo from '@/assets/img/ancv/turkish.png'
import VoloteaLogo from '@/assets/img/ancv/volotea.png'
import VuelingLogo from '@/assets/img/ancv/vueling.png'
import OuestFranceLogo from '@/assets/img/ancv/ouestfrance.svg'
import France2Logo from '@/assets/img/ancv/france2.svg'
import GraziaLogo from '@/assets/img/ancv/grazia.svg'
import BFMLogo from '@/assets/img/ancv/bfm.svg'
import EchosLogo from '@/assets/img/ancv/lesechos.svg'
import { BestOfferWidget } from '@/components/best-offers/BestOfferWidget'
import { useRouter } from 'next/router'
import ANCVSticy from '@/v2/components/ui/ANCVSticky'

function ANCV({ faq }) {
  const { t } = useTranslation()
  const { query } = useRouter()
  const explainBlock = useRef(undefined)
  const topButton = useRef()
  const connectURL = '/ancv-connect?' + new URLSearchParams(query)

  return (
    <main className="px-30 max-w-[1080px] mx-auto">
      <Head
        title={t('pages.checksLanding.seoTitle')}
        description={t('pages.checksLanding.seoTitle')}
      />
      <Script src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js" />
      <header className="text-center">
        <h1 className="text-dark opacity-50 text-18">Chèques-Vacances</h1>
        <h2 className="text-30 md:text-40 text-dark font-black leading-tight">
          Réservez vos billets d’avion avec vos{' '}
          <br className="hidden md:block" /> Chèques-Vacances Classic
        </h2>

        <div
          className="flex w-full gap-15 mt-30 md:max-w-[600px] mx-auto"
          ref={topButton}
        >
          <Button href="/ancv/form" className="flex-1">
            Remplir le formulaire
          </Button>
          <Button
            outline
            className="flex-1"
            onClick={() => {
              if (explainBlock.current) {
                explainBlock.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
              }
            }}
          >
            En savoir plus
          </Button>
        </div>

        <ANCVSticy toObserve={topButton} url="/ancv/form" />

        <p className="mt-20 text-14 text-dark-80 opacity-90">
          Pour les plus mordernes d’entre vous, nous acceptons aussi les
        </p>
        <Link
          className="text-14 text-dark-80 underline font-bold hover:no-underline"
          href={connectURL}
        >
          Chèques-Vacances Connect
        </Link>

        <LogoANCVClassic className="mx-auto mt-20" />
      </header>

      <section>
        <div className="mx-auto max-w-[800px] relative z-4">
          <Image src={HeaderANCV} alt="HeaderANCV" className="relative z-4" />
        </div>
      </section>

      <section className="text-center mt-[70px]">
        <h3 className="text-dark text-35 leading-tight">Déjà</h3>
        <h3 className="text-dark font-bold text-60 md:text-80">254 679</h3>
        <div className="text-dark text-25 md:text-30 font-normal">
          chèques vacances reçus
        </div>
        <div className="text-dark-60 text-18 font-normal">
          (oui, nous sommes des pros).
        </div>
      </section>

      <section className="mt-30">
        <TrustpilotCarousel tags="ANCV" />
      </section>

      <section className="mt-70" ref={explainBlock} id="how-to">
        <h3 className="text-30 md:text-35 text-dark-100 font-bold md:text-center">
          Chèques-Vacances Classic :<br className="hidden md:block" /> comment
          faire ?
        </h3>

        <div className="bg-[#F4F5FA] rounded-20 p-30 text-17 mt-30">
          <div className="max-w-[700px] mx-auto font-normal">
            💡 Nous vous conseillons de les échanger contre des{' '}
            <a className="font-bold">Chèques-Vacances Connect</a> sur la
            plateforme ANCV car le délai de traitement est moins long.{' '}
            <Link
              className="font-bold underline"
              href="https://cheque-vacances.com/aide/changer-format-classic-vers-connect/"
            >
              La procédure est juste ici.
            </Link>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 justify-evenly border-[#F3F4F9] border-b">
        <div className="md:px-40 pb-60 pt-40">
          <div className="rounded-20 bg-[#F3F4F9] flex flex-col justify-center text-center w-80 h-80 text-30 text-dark-80">
            1
          </div>

          <h4 className="text-22 mt-30 font-normal leading-tight md:min-h-[80px]">
            Payez votre vol avec votre CB sur notre site en 3 min
          </h4>
          <p className="text-dark-80 font-normal leading-relaxed opacity-90 text-16 mt-30 mb-30 min-h-[210px]">
            Rien de plus simple ! Vous n’avez qu’à réserver votre vol pour la
            destination de votre choix. Vous pouvez le faire pour n’importe quel
            voyageur et même si le montant est supérieur aux Chèques-Vacances
            que vous possédez.
          </p>

          <Button
            href="/"
            size="xl"
            color="light"
            className="opacity-90 w-full"
            outline
          >
            Réserver un vol
          </Button>
        </div>
        <div className="md:px-40 pb-60 pt-40 md:border-x md:border-[#F3F4F9]">
          <div className="rounded-20 bg-[#F3F4F9] flex flex-col justify-center text-center w-80 h-80 text-30 text-dark-80 font-normal">
            2
          </div>

          <h4 className="text-22 mt-30 font-normal leading-tight md:min-h-[80px]">
            Envoyez-nous vos chèques vacances
          </h4>
          <p className="text-dark-80 font-normal leading-relaxed opacity-90 text-16 mt-30 mb-30 min-h-[210px]">
            Faites une demande de formulaire en indiquant que vous avez déjà
            réservé votre voyage. Il faudra envoyer ensuite vos Chèques-Vacances
            et le formulaire en courrier recommandé avec avis de réception à
            notre adresse.
          </p>

          <Button
            href="/ancv/form"
            size="xl"
            color="primary"
            className="w-full"
          >
            Remplir le formulaire
          </Button>
        </div>
        <div className="md:px-40 pb-60 pt-40">
          <div className="rounded-20 bg-[#F3F4F9] flex flex-col justify-center text-center w-80 h-80 text-30 text-dark-80">
            3
          </div>

          <h4 className="text-22 mt-30 font-normal leading-tight md:min-h-[80px]">
            Vous êtes remboursés sous 30 jours ! 🥳
          </h4>
          <p className="text-dark-80 font-normal leading-relaxed opacity-90 text-16 mt-30">
            Une fois les chèques vacances reçus et traités, nous vous
            remboursons sous 30 jours sur la carte bancaire utilisée pour le
            paiement de votre voyage.
          </p>
        </div>
      </section>

      <BestOfferWidget className="grid grid-cols-1 lg:grid-cols-2 gap-30 mb-30 py-60 border-b border-[#F3F4F9]" />

      <section className="hidden md:flex mt-30 border-[#F3F4F9] border-b items-center pb-20">
        <div className="max-w-[90px] mr-10">
          <Image src={ANCVMobile} alt="ANCVMobile" />
        </div>

        <div>
          <p className="text-dark text-20 font-bold">
            Vous avez des Chèques-Vacances Connect ?<br /> On sait faire aussi !
          </p>
          <p className="mt-10 text-dark-80 opacity-80 font-normal text-17">
            Le process est tout aussi simple. Jetez un coup d’oeil ici{' '}
          </p>
        </div>

        <div className="flex-1"></div>

        <div>
          <Button
            color="dark"
            size="xl"
            className="min-w-[250px]"
            href={connectURL}
          >
            En savoir plus
          </Button>
        </div>
      </section>

      <section className="md:hidden mt-30 border-[#F3F4F9] border-b items-center pb-20">
        <div className="flex items-center">
          <div className="max-w-[70px] mr-10">
            <Image src={ANCVMobile} alt="ANCVMobile" />
          </div>

          <p className=" mt-10text-dark text-20 font-bold">
            Vous avez des Chèques-Vacances Connect ?<br /> On sait faire aussi !
          </p>
        </div>

        <p className="mt-10 text-dark-80 opacity-80 font-normal text-17">
          Le process est tout aussi simple. Jetez un coup d’oeil ici{' '}
        </p>

        <div className="flex mt-10">
          <Button color="dark" size="xl" className="w-full">
            En savoir plus
          </Button>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2">
        <div>
          <h4 className="mt-90 text-30 md:text-40 font-bold leading-tight mb-30">
            Envolez-vous où vous voulez{' '}
            <span className="text-blue">avec vos chèques vacances !</span>
          </h4>

          <h5 className="text-22 text-dark-100 mb-10 font-normal">
            N’importe quelle compagnie aérienne
          </h5>
          <p className="text-dark-100 opacity-60 text-17 font-normal">
            C’est comme si vous aviez des Chèques-Vacances Air France, EasyJet
            ou encore Emirates. Vous pouvez les utiliser sur toutes les
            compagnies aériennes proposées sur Ulysse et nous sommes partenaires
            de plus de 500 compagnies aériennes dans le monde. Donc vous avez le
            choix.
            <br /> <br />
            Sans l’embarras.
          </p>

          <h5 className="mt-20 text-22 text-dark-100 mb-10 font-normal">
            N’importe quelle destination
          </h5>

          <p className="text-dark-100 opacity-60 text-17 font-normal">
            Utilisez vos Chèque-Vacances Classic pour vous offrir le voyage de
            vos rêves. Le désert de Namibie, les côtes d’Italie, les montagnes
            en Argentine... Le monde est votre seule limite.
          </p>
        </div>

        <div className="hidden md:block pt-70 pl-30">
          <Image src={FlyWithANCV} alt="FlyWithANCV" />
        </div>
      </section>

      <section className="mt-80 pb-60">
        <div className="flex flex-wrap md:flex-nowrap gap-20 justify-center items-center">
          <div className="max-w-[200px] md:max-w-full">
            <Image src={AircanadaLogo} alt="Aircanada Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={AirfranceLogo} alt="Airfrance Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={TransaviaLogo} alt="Transavia Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={EasyjetLogo} alt="Easyjet Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={LufthansaLogo} alt="Lufthansa Logo" />
          </div>
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-20 justify-center items-center mt-30">
          <div className="max-w-[200px] md:max-w-full">
            <Image src={VuelingLogo} alt="Vueling Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={VoloteaLogo} alt="Volotea Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={QatarLogo} alt="Qatar Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={DeltaLogo} alt="Delta Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={TurkishLogo} alt="Turkish Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={KlmLogo} alt="Klm Logo" />
          </div>
          <div className="max-w-[200px] md:max-w-full">
            <Image src={BritishLogo} alt="British Logo" />
          </div>
        </div>
      </section>

      {faq ? (
        <section className="pt-50 pb-50">
          <h2 className="font-bold leading-none text-25 mb-20">
            Des questions ?
          </h2>
          <Faq faq={faq} />
        </section>
      ) : null}

      <section className="mt-20 mb-40">
        <div className="flex items-center mb-40">
          <h5 className="font-bold text-14 text-dark-60">
            Ils parlent de nous
          </h5>
          <div className="flex-1 pl-10 opacity-10">
            <div className="h-[1px] flex pl-30 bg-dark"></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-20 justify-center md:justify-between items-center">
          <OuestFranceLogo />
          <France2Logo />
          <GraziaLogo />
          <BFMLogo />
          <EchosLogo />
        </div>
      </section>
    </main>
  )
}

/**
 * Get the server side propos used to display this page (executed on the server side only)
 * @param {*} args metadata
 * @returns
 */
export const getServerSideProps = async ({ locale }) => {
  if (locale !== 'fr') {
    return {
      redirect: {
        destination: '/',
      },
    }
  }

  try {
    const { faqs } = await fetchBretzelRes('ancv')
    return {
      props: { faq: faqs.find((f) => f.slug === 'ancv-landing') },
    }
  } catch (err) {
    console.error(err)
    return {
      props: {},
    }
  }
}

ANCV.layout = CenterLayout
export default ANCV
